"use client";

import { useMemo, useState } from "react";
import { Euro } from "lucide-react";
import { FACTS } from "@/lib/facts";

/* ─────────────────────────────────────────────────────────────
 * Lizenz-Kosten-Rechner (bleibt laut Entscheidung Tobias: Kunden sollen
 * selbst sehen, was sie an Lizenzen ausgeben).
 *
 * Die Spanne für eine eigene Lösung kommt aus src/lib/facts.ts
 * (FACTS.customSoftwareRangeEur – Bestätigung Team ausstehend). Die
 * laufenden Kosten für Betrieb und Wartung sind eine Annahme für das
 * Amortisations-Beispiel und werden als solche beschriftet.
 * ──────────────────────────────────────────────────────────── */
const CUSTOM_BUILD_FROM = FACTS.customSoftwareRangeEur.from;
const CUSTOM_BUILD_TO = FACTS.customSoftwareRangeEur.to;
/** Annahme für Betrieb und Wartung je Monat (nur für die Amortisationsrechnung). */
const CUSTOM_MONTHLY = 200;

export function LicenseCalculator() {
  const [users, setUsers] = useState(25);
  const [pricePerUser, setPricePerUser] = useState(49);

  const monthly = users * pricePerUser;
  const yearly = monthly * 12;
  const fiveYears = yearly * 5;

  const breakEvenMonths = useMemo(() => {
    const customAvg = (CUSTOM_BUILD_FROM + CUSTOM_BUILD_TO) / 2;
    if (monthly <= CUSTOM_MONTHLY) return null; // Lizenzen bleiben günstiger
    return Math.ceil(customAvg / (monthly - CUSTOM_MONTHLY));
  }, [monthly]);

  return (
    <div
      className="relative overflow-hidden rounded-2xl border p-5 lg:p-8"
      style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)", boxShadow: "var(--card-shadow-lg)" }}
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand-400/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-rose-400/10 blur-3xl" />

      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold" style={{ backgroundColor: "var(--fi-error-bg)", color: "var(--fi-error-fg)" }}>
          <Euro className="h-3.5 w-3.5" aria-hidden="true" />
          Rechner
        </span>
        <h2 id="rechner" className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl" style={{ color: "var(--text)", letterSpacing: "-0.02em" }}>
          Wie viel geben Sie gerade für Lizenzen aus?
        </h2>
        <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
          Schieben Sie die Regler auf Ihre Zahlen: Wie viele Mitarbeitende haben Zugang zu Ihrem CRM, Projekt-, Buchhaltungs-
          oder Branchen-Programm? Und was kostet eine Lizenz pro Person und Monat? Der Rechner zeigt, was Sie in fünf Jahren
          bezahlt haben werden.
        </p>

        <div className="mt-8 grid gap-6 lg:grid-cols-2 lg:gap-10">
          <div className="space-y-6">
            <SliderField id="users" label="Nutzer mit Lizenz" value={users} min={5} max={150} step={1} format={(v) => `${v} Mitarbeitende`} onChange={setUsers} accent="brand" />
            <SliderField id="price" label="Lizenzpreis pro Nutzer und Monat" value={pricePerUser} min={10} max={200} step={1} format={(v) => `${v} €`} onChange={setPricePerUser} accent="error" />
            <div className="rounded-xl border p-4 text-xs leading-relaxed" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)", color: "var(--text-tertiary)" }}>
              <p>
                Der Regler deckt die übliche Spanne ab – von einfachen Büro-Lizenzen bis zu Unternehmens-CRMs. Ihren tatsächlichen
                Preis finden Sie auf Ihrer Rechnung.
              </p>
            </div>
          </div>

          <div className="flex flex-col justify-center gap-4 rounded-xl p-5 lg:p-6" style={{ backgroundColor: "var(--bg-secondary)" }}>
            <ResultRow label="Pro Monat" value={monthly} tone="muted" />
            <ResultRow label="Pro Jahr" value={yearly} tone="warning" />
            <ResultRow label="In 5 Jahren" value={fiveYears} tone="error" bold />

            <div className="mt-3 rounded-lg border-l-4 p-4" style={{ borderColor: "var(--fi-brand-fg)", backgroundColor: "var(--fi-brand-bg)" }}>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--fi-brand-fg)" }}>
                Stattdessen: Ihre eigene Lösung
              </p>
              <p className="mt-1 text-xl font-bold" style={{ color: "var(--fi-brand-fg)" }}>
                {CUSTOM_BUILD_FROM.toLocaleString("de-DE")} – {CUSTOM_BUILD_TO.toLocaleString("de-DE")} €
                <span className="text-sm font-medium"> einmalig</span>
              </p>
              <p className="mt-1 text-xs leading-snug" style={{ color: "var(--fi-brand-fg)" }}>
                zuzüglich Betrieb und Wartung (Annahme: {CUSTOM_MONTHLY} € je Monat)
                {breakEvenMonths !== null && (
                  <>
                    {" "}
                    · <strong>amortisiert nach {breakEvenMonths} {breakEvenMonths === 1 ? "Monat" : "Monaten"}</strong>
                  </>
                )}
              </p>
            </div>

            <p className="text-xs leading-snug" style={{ color: "var(--text-tertiary)" }}>
              Die Spanne enthält Konzept, Umsetzung der ersten Version und Übergabe mit Dokumentation – nicht den laufenden
              Betrieb, Erweiterungen oder Lizenzen Dritter. Sie ist eine Orientierung für einen typischen Funktionsumfang,
              kein Angebot: Ihren Festpreis kalkulieren wir im Erstgespräch.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SliderField({
  id,
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
  accent,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  onChange: (v: number) => void;
  accent: "brand" | "error";
}) {
  const bg = accent === "brand" ? "var(--fi-brand-bg)" : "var(--fi-error-bg)";
  const fg = accent === "brand" ? "var(--fi-brand-fg)" : "var(--fi-error-fg)";
  return (
    <div>
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-sm font-semibold" style={{ color: "var(--text)" }}>
          {label}
        </label>
        <span className="rounded-md px-2 py-0.5 text-sm font-bold tabular-nums" style={{ backgroundColor: bg, color: fg }}>
          {format(value)}
        </span>
      </div>
      <input id={id} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseInt(e.target.value))} className="mt-2 w-full accent-brand-500" />
      <div className="mt-1 flex justify-between text-xs" style={{ color: "var(--text-tertiary)" }}>
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}

function ResultRow({ label, value, tone, bold }: { label: string; value: number; tone: "muted" | "warning" | "error"; bold?: boolean }) {
  const color = tone === "error" ? "var(--fi-error-fg)" : tone === "warning" ? "var(--fi-warning-fg)" : "var(--text)";
  return (
    <div className="flex items-baseline justify-between gap-3">
      <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>
        {label}
      </p>
      <p className={`tabular-nums ${bold ? "text-2xl font-bold sm:text-3xl" : "text-xl font-semibold"}`} style={{ color }}>
        {value.toLocaleString("de-DE", { maximumFractionDigits: 0 })} €
      </p>
    </div>
  );
}
